import {
    FONT_SIZE,
    FONT_WEIGHT,
    LINE_HEIGHT,
    SPACING,
    PADDING,
    MARGIN,
    GAP,
    COMPONENT_SIZES,
    Z_INDEX,
    BORDERS,
    ELEVATION_STYLES,
    BUTTON_BORDER_STYLES,
    TRANSITION_TIMING,
    TRANSITION_EASING,
    TRANSITIONS,
    ANIMATION_TIMING,
    ANIMATIONS,
    BREAKPOINTS_VALUES,
    MEDIA_QUERIES,
    ICON_SIZES,
    ICON_STROKE_WIDTH,
} from "../definitions";

export const LIQUID_GLASS_COLORS = {
    primary: "#00629c",
    accent: "#00629c",
    secondary: "#00677e",
    warning: "#f59e0b",
    discount: "#f59e0b",

    white: "#ffffff",
    black: "#1a1c1e",

    greyLight: "rgba(255, 255, 255, 0.40)",
    greyMedium: "rgba(26, 28, 30, 0.20)",
    greyDark: "rgba(26, 28, 30, 0.40)",
    greyExtraDark: "rgba(26, 28, 30, 0.60)",

    success: "#4caf50",
    error: "#d32f2f",
    info: "#00629c",

    overlay: {
        dark: "rgba(0,58,92,0.06)",
        darkStrong: "rgba(0,58,92,0.12)",
        light: "rgba(255,255,255,0.50)",
        veryLight: "rgba(255,255,255,0.25)",
    },

    text: {
        primary: "#1a1c1e",
        secondary: "rgba(26, 28, 30, 0.55)",
        disabled: "rgba(26, 28, 30, 0.30)",
        light: "rgba(26, 28, 30, 0.65)",
        lighter: "rgba(26, 28, 30, 0.45)",
    },

    background: {
        paper: "rgba(255, 255, 255, 0.70)",
        default: "#f9f9fc",
    },

    glass: {
        surface: "rgba(255, 255, 255, 0.70)",
        surfaceHover: "rgba(255, 255, 255, 0.85)",
        surfaceStrong: "rgba(255, 255, 255, 0.90)",
        border: "rgba(255, 255, 255, 0.40)",
        borderStrong: "rgba(255, 255, 255, 0.60)",
        shadow: "rgba(0, 58, 92, 0.10)",
        shadowStrong: "rgba(0, 58, 92, 0.18)",
        buttonDark: "#00629c",
        buttonDarkText: "#ffffff",
        cream: "#f9f9fc",
        creamLight: "#ffffff",
    },
} as const;

const LIQUID_GLASS_FONT_FAMILY = {
    primary: '"Plus Jakarta Sans", "Manrope", system-ui, sans-serif',
    secondary: '"Manrope", system-ui, sans-serif',
    mono: "ui-monospace, 'SF Mono', Menlo, monospace",
} as const;

const LIQUID_GLASS_BORDER_RADIUS = {
    none: "0",
    xs: "12px",
    sm: "16px",
    md: "24px",
    lg: "32px",
    xl: "40px",
    pill: "100px",
    round: "50%",
} as const;

const LIQUID_GLASS_SHADOWS = {
    none: "none",
    xs: "0 4px 12px rgba(0,58,92,0.06)",
    sm: "0 6px 20px -4px rgba(0,58,92,0.08)",
    md: "0 12px 40px -12px rgba(0,58,92,0.10), inset 0 1px 0 rgba(255,255,255,0.85)",
    lg: "0 16px 48px -12px rgba(0,58,92,0.14), inset 0 1px 0 rgba(255,255,255,0.95)",
    xl: "0 24px 64px -16px rgba(0,58,92,0.18), inset 0 1px 0 rgba(255,255,255,0.95)",
} as const;

export const THEME_LIQUID_GLASS = {
    name: "liquidGlass",
    label: "Template configurable",

    colors: LIQUID_GLASS_COLORS,

    typography: {
        fontFamily: LIQUID_GLASS_FONT_FAMILY,
        fontSize: FONT_SIZE,
        fontWeight: FONT_WEIGHT,
        lineHeight: LINE_HEIGHT,
        styles: {
            h1: { fontSize: "2.5rem", fontWeight: 600, letterSpacing: "0.06em" },
            h2: { fontSize: "2rem", fontWeight: 600, letterSpacing: "0.04em" },
            h3: { fontSize: "1.5rem", fontWeight: 500, letterSpacing: "0.02em" },
            body: { fontSize: "1rem", fontWeight: 400, letterSpacing: "0.01em" },
            caption: { fontSize: "0.75rem", fontWeight: 400, letterSpacing: "0.04em" },
        },
    },

    spacing: {
        values: SPACING,
        padding: PADDING,
        margin: MARGIN,
        gap: GAP,
    },

    components: {
        sizes: COMPONENT_SIZES,
        zIndex: Z_INDEX,
    },

    visual: {
        shadows: LIQUID_GLASS_SHADOWS,
        borders: BORDERS,
        borderRadius: LIQUID_GLASS_BORDER_RADIUS,
        elevation: ELEVATION_STYLES,
        buttonBorders: BUTTON_BORDER_STYLES,
    },

    animation: {
        timing: TRANSITION_TIMING,
        easing: TRANSITION_EASING,
        transitions: TRANSITIONS,
        animationTiming: ANIMATION_TIMING,
        animations: ANIMATIONS,
    },

    responsive: {
        breakpoints: BREAKPOINTS_VALUES,
        mediaQueries: MEDIA_QUERIES,
    },

    icons: {
        sizes: ICON_SIZES,
        strokeWidth: ICON_STROKE_WIDTH,
    },
} as const;

export type ThemeLiquidGlass = typeof THEME_LIQUID_GLASS;
